import { getStoryListFilters } from './storylist.reducer';
import { showSnackbarWithMessage } from '../Snackbar/snackbar.saga';
import { countryByShortcode } from '../../utils/country';
import { storyListCallOptions } from '../../services/storylist.services';
import { callApi } from '../api';
import {
  doStorySearch,
  getStoryList,
  hideStoryListLoading,
  pageClick,
  showStoryListLoading,
  storyListSelect,
  toggleStoryListFilter,
  updateStoryList,
  updateStoryListFilters,
} from './storylist.actions';
import { call, put, select, take } from 'redux-saga/effects';
import { normalize, schema } from 'normalizr';
import { updateClusters } from '../Entities/Clusters/clusters.actions';
import { updateStories } from '../Entities/Stories/stories.actions';
import {
  AttributeFilter,
  Element,
  Row,
  SourceCategory,
  Story,
  TargetCategory,
  StoryError,
} from '../Entities/Stories/stories.reducer';
import { push } from 'react-router-redux';
import {
  ApiAttributeFilter,
  ApiElement,
  ApiRow,
  ApiSourceCategory,
  ApiTargetCategory,
} from '../../services/story.services';
import { takeLatest } from 'redux-saga/effects';
import { map, isEmpty } from 'ramda';
import { DateTime } from 'luxon';

const normalizeAttributeFilters = (entity: any): AttributeFilter[] => {
  return entity.map((attributeFilter: ApiAttributeFilter) => {
    return {
      id: attributeFilter.id,
      sourceCategoryId: attributeFilter.source_category_id,
      attributeGroupId: attributeFilter.attribute_group_id,
      attributeIds: attributeFilter.attribute_ids,
    };
  });
};

const normalizeTargetCategories = (entity: any) => {
  return entity.map(
    (targetCategory: ApiTargetCategory): TargetCategory => {
      return {
        id: targetCategory.id,
        category_id: targetCategory.category_id,
        isPinned: targetCategory.is_pinned,
      };
    }
  );
};

const normalizeSourceCategories = (entity: any) => {
  return entity.map(
    (sourceCategory: ApiSourceCategory): SourceCategory => {
      return {
        id: sourceCategory.id,
        priceFrom: sourceCategory.price_from,
        priceTo: sourceCategory.price_to,
        categoryId: sourceCategory.category_id,
        attributeFilters: sourceCategory.attributeFilters
          ? normalizeAttributeFilters(sourceCategory.attributeFilters.data)
          : [],
      };
    }
  );
};

const collectErrorsAndWarnings = (
  errors: string[],
  warnings: string[]
): StoryError[] => {
  return [
    ...map(name => ({ name, isWarning: false }), errors),
    ...map(name => ({ name, isWarning: true }), warnings),
  ];
};

// TODO: Probably spread and just replace keys
const normalizeAppConfigs = (entity: any) => {
  const { errors } = entity;
  const realErrors = (errors && errors.errors) || [];
  const realWarnings = (errors && errors.warnings) || [];

  return {
    id: entity.id,
    app_id: entity.app_id,
    active_from: entity.active_from,
    active_to: entity.active_to,
    boost_from: entity.boost_from,
    boost_to: entity.boost_to,
    rebrushed_at: entity.rebrushed_at,
    is_valid: entity.is_valid,
    is_active: entity.is_active,
    is_live: entity.is_live,
    stream_category_id: entity.stream_category_id,
    stream_product_ids: entity.stream_product_ids,
    brand_ids: entity.brand_ids,
    idol_ids: entity.idol_ids,
    product_ids: entity.product_ids,
    outfit_ids: entity.outfit_ids,
    sourceCategories:
      entity.sourceCategories && entity.sourceCategories.data.length > 0
        ? normalizeSourceCategories(entity.sourceCategories.data)
        : [],
    targetCategories: entity.sourceCategories
      ? normalizeTargetCategories(entity.targetCategories.data)
      : [],
    errors: collectErrorsAndWarnings(realErrors, realWarnings),
  };
};

export const appConfigsSchema = new schema.Entity(
  'appConfigs',
  {},
  {
    processStrategy: normalizeAppConfigs,
    idAttribute: 'app_id',
  }
);

const normalizeClusters = (entity: any) => ({
  id: entity.id,
  headline: entity.headline,
  name: entity.name,
  subline: entity.subline,
  text: entity.text,
});

export const clusterSchema = new schema.Entity('cluster', undefined, {
  processStrategy: normalizeClusters,
  idAttribute: 'id',
});

const normalizeElements = (entity: any) => {
  return entity.map(
    (element: ApiElement): Element => {
      return {
        id: element.id,
        position: element.position,
        type: element.type,
        resourceType: element.resource_type,
        resourceId: element.resource_id,
        url: element.url,
        additionalResourceData: element.additional_resource_data
          ? {
              brandId: element.additional_resource_data.brand_id,
            }
          : void 0,
        headerImageBrandId: element.header_image_brand_id,
        largerDesktopTeaser: element.larger_desktop_teaser,
        appImage: element.appImage,
        mobileImage: element.mobileImage,
        desktopImage: element.desktopImage,
        mobileVideo: element.mobileVideo,
        desktopVideo: element.desktopVideo,
        text: element.text,
        trackingId: element.tracking_id,
      };
    }
  );
};

export const normalizeStoryRows = (entity: any) => {
  return entity.map(
    (row: ApiRow): Row => {
      return {
        id: row.id,
        position: row.position,
        elements: row.elements ? normalizeElements(row.elements.data) : [],
        type: row.type,
        mobileRatio: row.mobile_tile_ratio,
        desktopRatio: row.desktop_tile_ratio,
        doubleTileShifted: !!row.double_tile_shifted,
        rowsCount: row.row_count,
        layoutOrder: row.layout_order,
      };
    }
  );
};

export const normalizeStory = (entity: any): Story => {
  return {
    id: entity.id,
    name: entity.name,
    color: entity.color,
    type: entity.type,
    initialAppId: entity.initial_app_id,
    resourceId: entity.resource_id,
    resourceType: entity.resource_type,
    contentType: entity.content_type,
    headline: entity.headline,
    subline: entity.subline,
    headlineLong: entity.headline_long,
    sublineLong: entity.subline_long,
    gender: entity.gender,
    age_from: entity.age_from,
    age_to: entity.age_to,
    appConfigs: entity.appConfigs ? entity.appConfigs.data : [],
    clusters: entity.clusters ? entity.clusters.data : [],
    rows: entity.rows ? normalizeStoryRows(entity.rows.data) : [],
    headerImageBrandId: entity.header_image_brand_id,
    largerDesktopTeaser: entity.larger_desktop_teaser,
    appImage: entity.appImage,
    mobileImage: entity.mobileImage,
    desktopImage: entity.desktopImage,
    ownerImage: entity.ownerImage,
    isPrimary: entity.is_primary,
    isSponsored: entity.is_sponsored,
    isBasSponsored: entity.is_bas_sponsored,
    isSale: entity.is_sale,
    isPremium: entity.is_premium,
    isHidden: entity.is_hidden,
    isProductRowDisabled: entity.is_product_row_disabled,
    isSecondhand: entity.is_secondhand,
    clusterStory: entity.clusterStory
      ? { position: entity.clusterStory.position }
      : void 0,
  };
};

export const storyListSchema = new schema.Entity(
  'story',
  {
    clusters: [clusterSchema],
  },
  {
    processStrategy: normalizeStory,
    idAttribute: 'id',
  }
);

function* fetchStoryList(page?: number) {
  yield put(showStoryListLoading());
  const {
    storyList: { searchQuery, country, filters },
  } = yield select();
  const countryFilter = !isEmpty(country)
    ? { 'filter[app]': countryByShortcode(country).id }
    : {};
  const isActive = filters.active ? { 'filter[active]': 1 } : {};
  const isInactive = filters.inactive ? { 'filter[active]': 0 } : {};
  const now = DateTime.local().toSQL();

  let params = {
    ...(searchQuery ? { 'filter[search]': searchQuery } : {}),
    ...(filters.live ? { 'filter[live]': now } : {}),
    ...(filters.boosted ? { 'filter[boosted]': now } : {}),
    ...isActive,
    ...isInactive,
    ...(page ? { page: page } : {}),
    ...{ include: 'clusters,appConfigs' },
    ...countryFilter,
  };

  const data = yield call(callApi, storyListCallOptions(params));
  if (data && data.ok) {
    if (data.data && data.data.length === 0) {
      yield showSnackbarWithMessage('error', 'No story found');

      return;
    }

    const normalizedStoryList = normalize(data.data, {
      data: [storyListSchema],
    }) as any;

    yield put(
      updateStoryList({
        pagination: normalizedStoryList.result.meta.pagination,
        storyIds: normalizedStoryList.result.data,
      })
    );
    yield put(updateStories(normalizedStoryList.entities.story));
    yield put(updateClusters(normalizedStoryList.entities.cluster));
  } else {
    yield showSnackbarWithMessage('error', 'Could not load story list');
  }
  yield put(hideStoryListLoading());
}

function* getStories() {
  const {
    storyList: { pagination },
  } = yield select();
  yield fetchStoryList(pagination ? pagination.currentPage : 1);
}

export function* watchForStoryListLoad() {
  const worker = function*() {
    yield call(getStories);
  };

  yield takeLatest(getStoryList.type, worker);
}

export function* watchForStorySearch() {
  const worker = function*() {
    yield call(getStories);
  };

  yield takeLatest(doStorySearch.type, worker);
}

export function* watchForStoryListPagination() {
  while (true) {
    const {
      payload: { page },
    } = yield take(pageClick.type);

    yield fetchStoryList(page);
  }
}

export function* watchForStoryListSelect() {
  while (true) {
    const {
      payload: { storyRow },
    } = yield take(storyListSelect.type);
    yield put(push(`/stories/${storyRow.id}/edit`));
  }
}

export function* watchForStoryListToggleFilter() {
  while (true) {
    const {
      payload: { filterKey },
    } = yield take(toggleStoryListFilter.type);
    const state = yield select();
    const filters = getStoryListFilters(state);
    const updated = {
      ...filters,
      [filterKey]: !Boolean(filters[filterKey]),
    };
    yield put(updateStoryListFilters({ filters: updated }));
    yield call(fetchStoryList, 0);
  }
}



// WEBPACK FOOTER //
// ./src/store/StoryList/storylist.saga.ts